/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ScheduleTaskCcResourceListItemDto,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleTaskUserResourceListItemDto,
  ScheduleTemplateTaskDetailsDto,
  ScheduleTemplateTaskNoteListItemDto,
  ScheduleTemplateTaskTodoListItemDto,
  ScheduleType,
  TaskRelationDto,
  UserListFilterDto,
  UserListItemDto,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  createArrayHelper,
  EntityConfig,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  FormStateSnapshot,
  formStateSnapshot,
  getFormStateSnapshotValue,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithUniqueId, WithVersion } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { currentDate, sortByBoolField } from '@gh/core-util';

import { map, omit, pick, uniqueId } from 'lodash';

export type ScheduleTemplateTaskUserResourceListItem = ScheduleTaskUserResourceListItemDto;
export type ScheduleTemplateTaskCcResourceListItem = ScheduleTaskCcResourceListItemDto;
export type ScheduleTemplateTaskNoteListItem = WithUniqueId<ScheduleTemplateTaskNoteListItemDto>;
export type ScheduleTemplateTaskTodoListItem = ScheduleTemplateTaskTodoListItemDto;
export type ScheduleTemplatePredecessorListItem = TaskRelationDto;
export type ScheduleTaskUserAutocompleteFilter = UserListFilterDto;
export type ScheduleTaskUserAutocomplete = UserListItemDto;

export enum ScheduleTemplateTaskDetailsTabName {
  General = 'GENERAL',
  Notes = 'NOTES',
  Todos = 'TODOS',
}

export enum ScheduleTemplateTaskDetailsGeneralSubtabName {
  CcResource = 'CC_RESOURCE',
  UserResource = 'USER_RESOURCE',
}

export const DEFAULT_ADD_TODO_DAYS_CONTROL_VALUE: number = 2;

export type WithTab<T> = T & { selectedTab: ScheduleTemplateTaskDetailsTabName };
export type WithGeneralSubtab<T> = T & { selectedGeneralSubtab: Maybe<ScheduleTemplateTaskDetailsGeneralSubtabName> };

export type ScheduleTemplateTaskInfo = {
  name?: string; /* enabled: type!=CP!=CCO, params!=0 BitSet*/
  duration?: number;
  costCenterCode?: number; // defined by cost_center.code
  description?: string;
  resource?: string; // selected on CC Resource || Users
  changeDateAfterComplete?: ScheduleTaskChangeDateAfterCompleteType; // Template Type: Pre-Construction
  isExcludeFromGanttChart: boolean;
};

export type ScheduleTemplateTaskDetails = WithTab<WithGeneralSubtab<WithVersion<{
  info: FormStateSnapshot<ScheduleTemplateTaskInfo>;
  taskNumber?: number;
  type: ScheduleTaskType;
  isSlab: boolean;
  isContractStart: boolean;
  isFromCriticalPath: boolean;
  isIncludedTasksExist: boolean;
  isInSupervisorSchedule: boolean;
  ccResources?: ScheduleTemplateTaskCcResourceListItem[];
  employeeResources?: ScheduleTemplateTaskUserResourceListItem[];
  parentTaskNumber?: ID;
  templateTaskId?: ID;
  criticalPathId?: ID;
  notes: ScheduleTemplateTaskNoteListItem[];
  todo: WithUniqueId<ScheduleTemplateTaskTodoListItem>[];
  costCenterId?: ID;
  predecessors?: ScheduleTemplatePredecessorListItem[];
  supplierCostCenterId?: ID;
  supplierContactId?: ID;
  userId?: ID;
  startDate?: Date;
  endDate?: Date;
}>>>;

export const SCHEDULE_TEMPLATE_TASK_ENTITY_CONFIG = {
  isInSupervisorSchedule: { editable: true },
  isExcludeFromGanttChart: { editable: true },
};
export const scheduleTemplateTaskFieldState = fieldStateByEntityConfig(SCHEDULE_TEMPLATE_TASK_ENTITY_CONFIG);

export const isExistsCcResourceGeneralSubtab = (type: ScheduleTaskType, isIncludedTasksExist: boolean): boolean =>
  type === ScheduleTaskType.CostCenter || type === ScheduleTaskType.Empty
  || ((type === ScheduleTaskType.CriticalPath || type === ScheduleTaskType.Summary) && !isIncludedTasksExist);

export const isExistsUserResourceGeneralSubtab = (type: ScheduleTaskType, isIncludedTasksExist: boolean): boolean =>
  type === ScheduleTaskType.Empty || type === ScheduleTaskType.CostCenter || (type === ScheduleTaskType.Summary && !isIncludedTasksExist);

export const SCHEDULE_TEMPLATE_TASK_DETAILS_INIT: ScheduleTemplateTaskDetails = {
  info: formStateSnapshot({
    name: '',
    resource: void 0,
    duration: void 0,
    costCenterCode: 1,
    changeDateAfterComplete: ScheduleTaskChangeDateAfterCompleteType.FinanceApproved,
    isExcludeFromGanttChart: false,
    description: void 0,
  }),
  taskNumber: void 0,
  selectedTab: <any>void 0,
  selectedGeneralSubtab: <any>void 0,
  employeeResources: [],
  ccResources: [],
  parentTaskNumber: void 0,
  templateTaskId: void 0,
  criticalPathId: void 0,
  type: ScheduleTaskType.Empty,
  isSlab: false,
  isContractStart: false,
  isFromCriticalPath: false,
  isIncludedTasksExist: false,
  isInSupervisorSchedule: false,
  notes: [],
  todo: [],
  costCenterId: void 0,
  predecessors: [],
  supplierContactId: void 0,
  userId: void 0,
  startDate: void 0,
  endDate: void 0,
};

export const scheduleTemplateTaskDetailsFromDto = (dto: ScheduleTemplateTaskDetailsDto) => <ScheduleTemplateTaskDetails>({
  info: formStateSnapshot(pick(dto, [
    'name',
    'duration',
    'costCenterCode',
    'description',
    'resource',
    'changeDateAfterComplete',
    'isExcludeFromGanttChart',
  ])),
  selectedTab: ScheduleTemplateTaskDetailsTabName.General,
  selectedGeneralSubtab: isExistsCcResourceGeneralSubtab(dto.type, dto.isIncludedTasksExist)
    ? ScheduleTemplateTaskDetailsGeneralSubtabName.CcResource
    : isExistsUserResourceGeneralSubtab(dto.type, dto.isIncludedTasksExist)
      ? ScheduleTemplateTaskDetailsGeneralSubtabName.UserResource
      : void 0,
  ccResources: sortByBoolField(dto.ccResources || [], 'isPreferred', 'desc'),
  employeeResources: dto.employeeResources, // grid resource
  parentTaskNumber: dto.parentTaskNumber,
  templateTaskId: dto.templateTaskId,
  criticalPathId: dto.criticalPathId,
  type: dto.type,
  taskNumber: dto.taskNumber,
  isSlab: dto.isSlab,
  isContractStart: dto.isContractStart,
  isFromCriticalPath: dto.isFromCriticalPath,
  isIncludedTasksExist: dto.isIncludedTasksExist,
  isInSupervisorSchedule: dto.isInSupervisorSchedule,
  notes: map(dto.notes, (note: ScheduleTemplateTaskNoteListItem) =>
    ({ ...note, uniqueId: generateScheduleTemplateTaskNoteId() })),
  todo: map(dto.todo, (todo: ScheduleTemplateTaskTodoListItem) =>
    ({ ...todo, uniqueId: generateScheduleTemplateTaskTodoId() })),
  costCenterId: dto.costCenterId,
  predecessors: dto.predecessors,
  supplierContactId: dto.supplierContactId,
  userId: dto.employeeContactId,
  startDate: dto.startDate,
  endDate: dto.endDate,
  version: dto.version,
});

export const scheduleTemplateTaskDetailsToDto = (details: ScheduleTemplateTaskDetails) => {
  const info = getFormStateSnapshotValue(details.info);
  return <ScheduleTemplateTaskDetailsDto>{
    ...info,
    parentTaskNumber: details.parentTaskNumber,
    templateTaskId: details.templateTaskId,
    taskNumber: details.taskNumber,
    criticalPathId: details.criticalPathId,
    type: details.type,
    isSlab: details.isSlab,
    isContractStart: details.isContractStart,
    isFromCriticalPath: details.isFromCriticalPath,
    isInSupervisorSchedule: details.isInSupervisorSchedule,
    notes: map(details.notes, (note: any) => omit(note, ['uniqueId'])),
    todo: map(details.todo, (todo: any) => omit(todo, ['uniqueId'])),
    costCenterId: details.costCenterId,
    predecessors: info.isExcludeFromGanttChart ? [] : details.predecessors,
    supplierContactId: details.supplierContactId,
    employeeContactId: details.userId,
    startDate: details.startDate,
    endDate: details.endDate,
    version: details.version,
  };
};

export const getScheduleTemplateTaskDefaultGeneralSubTab =
  (taskDetails: ScheduleTemplateTaskDetails): Maybe<ScheduleTemplateTaskDetailsGeneralSubtabName> => ({
    [ScheduleTaskType.CriticalPath]: !taskDetails.isIncludedTasksExist
      ? ScheduleTemplateTaskDetailsGeneralSubtabName.CcResource
      : void 0,
    [ScheduleTaskType.CostCenter]: ScheduleTemplateTaskDetailsGeneralSubtabName.CcResource,
    [ScheduleTaskType.Summary]: !taskDetails.isIncludedTasksExist
      ? ScheduleTemplateTaskDetailsGeneralSubtabName.CcResource
      : void 0,
    [ScheduleTaskType.Empty]: ScheduleTemplateTaskDetailsGeneralSubtabName.CcResource,
  }[taskDetails.type]);

export const getScheduleTemplateTaskNoteId = ({ uniqueId: id }: ScheduleTemplateTaskNoteListItem) => id;
export const scheduleTemplateTaskNoteArr = createArrayHelper(getScheduleTemplateTaskNoteId);
export const generateScheduleTemplateTaskNoteId = () => uniqueId('schedule-task-note:');
export const createTaskNote = (memo?: string, addedBy?: string): ScheduleTemplateTaskNoteListItem => ({
  templateTaskNoteId: EMPTY_ID,
  uniqueId: generateScheduleTemplateTaskNoteId(),
  memo,
  addedDate: currentDate(),
  addedBy,
});

export const getScheduleTemplateTaskTodoId = ({ uniqueId: id }: WithUniqueId<ScheduleTemplateTaskTodoListItem>) => id;
export const scheduleTemplateTaskTodoArr = createArrayHelper(getScheduleTemplateTaskTodoId);
export const generateScheduleTemplateTaskTodoId = () => uniqueId('schedule-task-todo:');
export const createTaskTodo = ({ days, event, systemRole, user, memo }:
                                 ScheduleTemplateTaskTodoListItem): WithUniqueId<ScheduleTemplateTaskTodoListItem> => ({
  templateTaskTodoId: EMPTY_ID,
  uniqueId: generateScheduleTemplateTaskTodoId(),
  days,
  event,
  user,
  systemRole,
  addedDate: currentDate(),
  memo,
});

export type ScheduleTemplateTaskDetailsNoteEdit = {
  memo?: string;
};

export type ScheduleTemplateTaskTodoDialogConfig = {
  scheduleTemplateType: ScheduleType;
  templateTaskTodoId?: ID;
  days?: number;
  event: ScheduleTaskTodoEventType;
  systemRole: UserSystemRole;
  userId?: ID;
  memo?: string;
  mode: FormMode;
};

export const SCHEDULE_TEMPLATE_TASK_INFO_ENTITY_CONFIG: EntityConfig<ScheduleTemplateTaskInfo> = {
  name: { required: true },
};
export const validateScheduleTemplateTaskInfo = entityValidatorByEntityConfig(SCHEDULE_TEMPLATE_TASK_INFO_ENTITY_CONFIG);
